import { useState, useEffect, useCallback } from "react";
import { MediaDevicesEvents, MediaDevices } from "../../utils/media-devices-utils";
import { getLocalVideoSharingSource, getLocalVideoSharingSources } from "../../utils/local-video-share-utils";
import useAvatar from "./useAvatar";
import { RoomResourceIds } from "../../utils/room-resource-state-utils";
import { resolveMediaActionState } from "./media-resource-state-utils";
import { useLocalMediaResourceAvailability } from "./useLocalMediaResourceAvailability";

function addSource(sources, source) {
  if (!source || sources.includes(source)) {
    return sources;
  }

  return [...sources, source];
}

function removeSource(sources, source) {
  if (!source) {
    return [];
  }

  return sources.filter(existingSource => existingSource !== source);
}

export function useShare(scene, hubChannel) {
  const mediaDevicesManager = APP.mediaDevicesManager;
  const [sharingSources, setSharingSources] = useState([]);
  const [canShareCamera, setCanShareCamera] = useState(false);
  const [canShareScreen, setCanShareScreen] = useState(false);
  const [canShareCameraToAvatar, setCanShareCameraToAvatar] = useState(false);
  const { hasVideoTextureTarget } = useAvatar();
  const isSharingCamera = sharingSources.includes(MediaDevices.CAMERA);
  const isSharingScreen = sharingSources.includes(MediaDevices.SCREEN);
  const { unavailableReason: cameraUnavailableReason } = useLocalMediaResourceAvailability(RoomResourceIds.CAMERA, {
    label: "Camera",
    locallyOwned: isSharingCamera
  });
  const { unavailableReason: screenShareUnavailableReason } = useLocalMediaResourceAvailability(
    RoomResourceIds.SCREEN_SHARE,
    {
      label: "Screen share",
      locallyOwned: isSharingScreen
    }
  );
  const cameraActionState = resolveMediaActionState({
    locallyOwned: isSharingCamera,
    active: isSharingCamera,
    unsupported: !canShareCamera && !isSharingCamera,
    unavailableReason: cameraUnavailableReason
  });
  const screenShareActionState = resolveMediaActionState({
    locallyOwned: isSharingScreen,
    active: isSharingScreen,
    unsupported: !canShareScreen && !isSharingScreen,
    unavailableReason: screenShareUnavailableReason
  });

  useEffect(() => {
    function onShareVideoEnabled(event) {
      setSharingSources(sources => addSource(sources, event.detail?.source));
    }

    function onShareVideoDisabled(event) {
      setSharingSources(sources => removeSource(sources, event.detail?.source));
    }

    function onPermissionsUpdated() {
      const canShareMedia = hubChannel.can("spawn_and_move_media");

      if (canShareMedia) {
        navigator.mediaDevices
          .enumerateDevices()
          .then(devices => {
            const hasCamera = devices.some(device => device.kind === "videoinput");
            setCanShareCamera(hasCamera);
            setCanShareCameraToAvatar(hasCamera && hasVideoTextureTarget);
          })
          .catch(() => {
            setCanShareCamera(false);
            setCanShareCameraToAvatar(false);
          });

        setCanShareScreen(!!navigator.mediaDevices.getDisplayMedia);
      } else {
        setCanShareScreen(false);
        setCanShareCamera(false);
        setCanShareCameraToAvatar(false);
      }
    }

    scene.addEventListener("share_video_enabled", onShareVideoEnabled);
    scene.addEventListener("share_video_disabled", onShareVideoDisabled);
    // TODO: Show share error dialog
    scene.addEventListener("share_video_failed", onShareVideoDisabled);
    hubChannel.addEventListener("permissions_updated", onPermissionsUpdated);

    onPermissionsUpdated();

    setSharingSources(getLocalVideoSharingSources(mediaDevicesManager));

    return () => {
      scene.removeEventListener("share_video_enabled", onShareVideoEnabled);
      scene.removeEventListener("share_video_disabled", onShareVideoDisabled);
      scene.removeEventListener("share_video_failed", onShareVideoDisabled);
      hubChannel.removeEventListener("permissions_updated", onPermissionsUpdated);
    };
  }, [scene, hubChannel, hasVideoTextureTarget, mediaDevicesManager]);

  const sharingSource =
    getLocalVideoSharingSource({ mediaStream: mediaDevicesManager?.mediaStream }) || sharingSources[0] || null;

  const toggleShareCamera = useCallback(() => {
    if (isSharingCamera) {
      scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: MediaDevices.CAMERA });
    } else {
      scene.emit("action_share_camera");
    }
  }, [scene, isSharingCamera]);

  const toggleShareScreen = useCallback(() => {
    if (isSharingScreen) {
      scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: MediaDevices.SCREEN });
    } else {
      scene.emit("action_share_screen");
    }
  }, [scene, isSharingScreen]);

  const toggleShareCameraToAvatar = useCallback(() => {
    if (isSharingCamera) {
      scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source: MediaDevices.CAMERA });
    } else {
      scene.emit("action_share_camera", { target: "avatar" });
    }
  }, [scene, isSharingCamera]);

  return {
    sharingSource,
    sharingSources,
    canShareCamera,
    canShareCameraToAvatar,
    canShareScreen,
    cameraUnavailableReason,
    cameraActionState,
    screenShareUnavailableReason,
    screenShareActionState,
    toggleShareCamera,
    toggleShareCameraToAvatar,
    toggleShareScreen,
    isSharingCamera,
    isSharingScreen
  };
}
